import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';

import LogoutIcon from '@mui/icons-material/Logout';
import LoopIcon from '@mui/icons-material/Loop';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { getAppConfig } from '@/config/AppConfig';
import { useInlineMemo } from '@/util/useMemoStyles';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMemo, useCallback, useState } from 'react';
import {
  useRecordContext,
  useRefresh,
  Labeled,
  UpdateButton,
} from 'react-admin';
import { Visibility } from '@mui/icons-material';
import ReactJson from 'react-json-view';

const appConfig = getAppConfig();

export const ForceLogout = () => {
  const refresh = useRefresh();

  const therapist = useRecordContext();

  const forceLogout = useMemo(
    () => therapist?.forceLogout || {},
    [therapist],
  );

  const [outputDialogOpen, setOutputDialogOpen] = useState(false);

  const onViewOutput = useCallback(() => {
    setOutputDialogOpen(true);
  }, []);

  const onCloseOutput = useCallback(() => {
    setOutputDialogOpen(false);
  }, []);

  if (!therapist) {
    return null;
  }

  return (
    <>
      <Box paddingTop="1.5rem" display="flex">
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          paddingTop=".5rem"
        >
          <Labeled label="Last Requested At">
            {forceLogout.lastRequestedAt ? (
              <GenericRelativeDateDisplay
                date={forceLogout.lastRequestedAt}
              />
            ) : (
              <Typography>Never</Typography>
            )}
          </Labeled>
          {!!forceLogout.input && (
            <>
              <Labeled marginLeft="1rem" label="Mode">
                <Chip label={forceLogout.input.mode} />
              </Labeled>
              {forceLogout.input.mode === 'hard' &&
                forceLogout.input.resetFileSystem && (
                  <Labeled
                    marginLeft="1rem"
                    label="Reset File System"
                  >
                    <Chip label="Yes" />
                  </Labeled>
                )}
            </>
          )}
          <Labeled marginLeft="1rem" label="Last Completed At">
            {forceLogout.lastCompletedAt ? (
              <GenericRelativeDateDisplay
                date={forceLogout.lastCompletedAt}
              />
            ) : (
              <Typography>Never</Typography>
            )}
          </Labeled>
          {forceLogout.output && (
            <Box marginLeft="1rem">
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={onViewOutput}
              >
                <Visibility />
              </Button>
            </Box>
          )}
          <Box marginLeft="1rem">
            <ForceSoftLogoutButton
              therapistId={therapist.id as any}
            />
            <ForceHardLogoutButton
              therapistId={therapist.id as any}
            />
            <ForceSystemResetButton
              therapistId={therapist.id as any}
            />
          </Box>
        </Box>
      </Box>
      <Dialog open={outputDialogOpen} onClose={onCloseOutput}>
        <DialogTitle>Force Logout Output</DialogTitle>
        <DialogContent>
          <ReactJson
            src={forceLogout.output}
            theme="monokai"
            displayDataTypes={false}
            enableClipboard={true}
            collapsed={2}
            style={{
              backgroundColor: 'transparent',
              fontFamily: 'monospace',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseOutput}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export type SystemSnapshotButtonProps = {
  therapistId: string;
  label?: string;
  confirmTitle?: string;
  confirmContent?: string;
};

export type ForeignEntitySystemSnapshotProps = {
  therapistId: string;
};

export const ForceSoftLogoutButton = ({
  therapistId,
  label = 'Force Soft Logout',
  confirmTitle = 'Are you sure?',
  confirmContent = "This will force a soft logout of the Therapist's device. This is perfectly safe, but please use it sparingly to avoid disrupting the user experience.",
}: SystemSnapshotButtonProps) => {
  const refresh = useRefresh();

  return (
    <UpdateButton
      label={label}
      resource="therapists"
      id={therapistId}
      color="warning"
      variant="outlined"
      mutationMode="pessimistic"
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      icon={<LoopIcon />}
      sx={{ marginLeft: '1rem' }}
      mutationOptions={{ onSuccess: refresh }}
      data={useInlineMemo({
        forceLogout: JSON.stringify({
          lastRequestedAt: new Date().toISOString(),
          lastCompletedAt: null,
          input: {
            mode: 'soft',
          },
        }),
      })}
    />
  );
};

export const ForceHardLogoutButton = ({
  therapistId,
  label = 'Force Hard Logout',
  confirmTitle = 'Are you sure?',
  confirmContent = "This will force a HARD logout of the Therapist's device. This will cause the local database files to be deleted. ANY UNSYNCED DATA WILL BE LOST. Don't do this unless you absolutely know what you're doing.",
}: SystemSnapshotButtonProps) => {
  const refresh = useRefresh();

  return (
    <UpdateButton
      label={label}
      resource="therapists"
      id={therapistId}
      color="warning"
      variant="outlined"
      mutationMode="pessimistic"
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      icon={<LogoutIcon />}
      sx={{ marginLeft: '1rem' }}
      mutationOptions={{ onSuccess: refresh }}
      data={useInlineMemo({
        forceLogout: JSON.stringify({
          lastRequestedAt: new Date().toISOString(),
          lastCompletedAt: null,
          input: {
            mode: 'hard',
          },
        }),
      })}
    />
  );
};

export const ForceSystemResetButton = ({
  therapistId,
  label = 'Force System Reset',
  confirmTitle = 'Are you sure?',
  confirmContent = 'This perform a HARD Logout AND delete all files in the local filesystem. ALL DATA STORED LOCALLY WILL BE ERASED.',
}: SystemSnapshotButtonProps) => {
  const refresh = useRefresh();

  return (
    <UpdateButton
      label={label}
      resource="therapists"
      id={therapistId}
      color="warning"
      variant="outlined"
      mutationMode="pessimistic"
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      icon={<DeleteForeverIcon />}
      sx={{ marginLeft: '1rem' }}
      mutationOptions={{ onSuccess: refresh }}
      data={useInlineMemo({
        forceLogout: JSON.stringify({
          lastRequestedAt: new Date().toISOString(),
          lastCompletedAt: null,
          input: {
            mode: 'hard',
            resetFileSystem: true,
          },
        }),
      })}
    />
  );
};
